// DASHBOARD PORTAL
// pages/Users/AddUser.js (UPDATED VERSION WITH TERMS)

import React, { useState, useEffect } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Chip,
  Divider,
  Grid,
  useTheme,
  ListSubheader,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from "@mui/material";

export default function AddUser({ onRefresh }) {
  const theme = useTheme();
  const [roles, setRoles] = useState([]);
  const [terms, setTerms] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [loadingTerms, setLoadingTerms] = useState(true);
  const [formData, setFormData] = useState({
    email: "",
    role_id: "",
    term_ids: [], // NEW: Selected terms
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    fetchRoles(controller);
    fetchTerms(controller);
    return () => controller.abort();
  }, []);

  const fetchRoles = async (controller) => {
    try {
      setLoadingRoles(true);
      const response = await axiosInstance.get("/api/roles", {
        signal: controller.signal,
      });

      if (response.data.success) {
        const responseData =
          typeof response.data.data === "string"
            ? JSON.parse(response.data.data)
            : response.data.data;

        const activeRoles = (responseData.roles || []).filter((r) => r.is_active);
        setRoles(activeRoles);

        const defaultRole = activeRoles.find((r) => r.name === "employee");
        if (defaultRole) {
          setFormData((prev) => ({ ...prev, role_id: defaultRole.id }));
        }
      }
    } catch (error) {
      if (error.name !== "CanceledError") {
        console.error("Error fetching roles:", error);
      }
    } finally {
      if (!controller.signal.aborted) {
        setLoadingRoles(false);
      }
    }
  };

  const fetchTerms = async (controller) => {
    try {
      setLoadingTerms(true);
      const response = await axiosInstance.get("/api/terms", {
        signal: controller.signal,
      });

      if (response.data.success) {
        // Only show active terms
        const activeTerms = (response.data.terms || []).filter((t) => t.is_active);
        setTerms(activeTerms);
      }
    } catch (error) {
      if (error.name !== "CanceledError") {
        console.error("Error fetching terms:", error);
      }
    } finally {
      if (!controller.signal.aborted) {
        setLoadingTerms(false);
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleTermsChange = (event) => {
    const {
      target: { value },
    } = event;
    setFormData((prev) => ({
      ...prev,
      term_ids: typeof value === "string" ? value.split(",") : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setIsLoading(true);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || !emailRegex.test(formData.email)) {
      setError("Please enter a valid email address");
      setIsLoading(false);
      return;
    }

    if (!formData.role_id) {
      setError("Please select a role");
      setIsLoading(false);
      return;
    }

    try {
      // Step 1: Create user
      const response = await axiosInstance.post("/api/users", {
        email: formData.email,
        role: "employee",
      });

      if (response.data.success) {
        const userData =
          typeof response.data.data === "string"
            ? JSON.parse(response.data.data)
            : response.data.data;

        try {
          // Step 2: Assign role
          await axiosInstance.post("/api/permissions/assign", {
            user_id: userData.user_id,
            role_id: formData.role_id,
          });

          // Step 3: Assign terms (if any selected)
          if (formData.term_ids.length > 0) {
            await axiosInstance.post("/api/terms/assign", {
              user_id: userData.user_id,
              term_ids: formData.term_ids,
            });
          }
        } catch (roleError) {
          console.warn("Role/Terms assignment failed:", roleError);
          setError(
            "User created but role/terms assignment failed. Please assign manually."
          );
        }

        setSuccess(
          `User invitation sent successfully! ${
            formData.term_ids.length > 0
              ? `${formData.term_ids.length} term(s) assigned.`
              : ""
          }`
        );
        setFormData({
          email: "",
          role_id: roles.find((r) => r.name === "employee")?.id || "",
          term_ids: [],
        });

        if (onRefresh) {
          setTimeout(() => {
            onRefresh();
            setSuccess(null);
          }, 3000);
        }
      } else {
        setError(response.data.message || "Failed to add user");
      }
    } catch (error) {
      setError(error.response?.data?.message || "Error adding user");
    } finally {
      setIsLoading(false);
    }
  };

  const globalRoles = roles.filter((r) => r.is_system_role);
  const customRoles = roles.filter((r) => !r.is_system_role);
  const selectedRole = roles.find((r) => r.id === parseInt(formData.role_id));

  const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      <Box sx={{ mb: 3, pb: 2, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
        <Typography sx={{ fontFamily, fontSize: '1.1rem', fontWeight: 600, color: '#1e293b' }}>
          Invite New User
        </Typography>
      </Box>
      <Grid container spacing={3}>
        {/* Left: Add user form */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              p: 3,
              borderRadius: '12px',
              bgcolor: 'rgba(139,92,246,0.03)',
              border: '1px solid rgba(0,0,0,0.06)',
            }}
          >
            <Typography sx={{ fontFamily, fontSize: '1rem', fontWeight: 600, color: '#1e293b', mb: 2 }}>
              User Details
            </Typography>

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              fullWidth
              margin="normal"
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              disabled={isLoading}
              required
              helperText="An invitation email will be sent to this address"
            />

            <FormControl fullWidth margin="normal" required>
              <InputLabel>Role</InputLabel>
              {loadingRoles ? (
                <Box textAlign="center" py={2}>
                  <CircularProgress size={25} />
                </Box>
              ) : (
                <Select
                  name="role_id"
                  label="Role"
                  value={formData.role_id}
                  onChange={handleChange}
                  disabled={isLoading}
                >
                  {globalRoles.length > 0 && [
                    <ListSubheader key="global-header">
                      Global Roles (Shared)
                    </ListSubheader>,
                    globalRoles.map((role) => (
                      <MenuItem key={role.id} value={role.id}>
                        {role.name.charAt(0).toUpperCase() + role.name.slice(1)}{" "}
                        (Global)
                      </MenuItem>
                    )),
                  ]}

                  {customRoles.length > 0 && [
                    <ListSubheader key="custom-header">
                      Custom Team Roles
                    </ListSubheader>,
                    customRoles.map((role) => (
                      <MenuItem key={role.id} value={role.id}>
                        {role.name.charAt(0).toUpperCase() + role.name.slice(1)}{" "}
                        (Custom)
                      </MenuItem>
                    )),
                  ]}
                </Select>
              )}
              <Typography variant="caption" color="text.primary">
                Select the user's permission level within your team
              </Typography>
            </FormControl>

            {/* NEW: Terms Selection */}
            <FormControl fullWidth margin="normal">
              <InputLabel>Terms of Use (Optional)</InputLabel>
              {loadingTerms ? (
                <Box textAlign="center" py={2}>
                  <CircularProgress size={25} />
                </Box>
              ) : (
                <Select
                  multiple
                  name="term_ids"
                  value={formData.term_ids}
                  onChange={handleTermsChange}
                  input={<OutlinedInput label="Terms of Use (Optional)" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => {
                        const term = terms.find((t) => t.id === value);
                        return (
                          <Chip
                            key={value}
                            label={term?.title || value}
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  )}
                  disabled={isLoading}
                >
                  {terms.length === 0 ? (
                    <MenuItem disabled>
                      <Typography variant="caption" color="text.secondary">
                        No active terms available
                      </Typography>
                    </MenuItem>
                  ) : (
                    terms.map((term) => (
                      <MenuItem key={term.id} value={term.id}>
                        <Checkbox
                          checked={formData.term_ids.indexOf(term.id) > -1}
                        />
                        <ListItemText primary={term.title} />
                      </MenuItem>
                    ))
                  )}
                </Select>
              )}
              <Typography variant="caption" color="text.primary">
                User must accept selected terms before accessing the workspace
              </Typography>
            </FormControl>

            {selectedRole && (() => {
              const roleColors = {
                admin: { bg: '#f3e8ff', color: '#7c3aed' },
                employee: { bg: '#dbeafe', color: '#2563eb' },
                customer: { bg: '#d1fae5', color: '#059669' },
                student: { bg: '#fef3c7', color: '#d97706' },
              };
              const roleKey = selectedRole.name?.toLowerCase();
              const roleStyle = roleColors[roleKey];
              const bothPanels = ['admin', 'employee'].includes(roleKey);
              return (
                <Alert
                  severity={selectedRole.is_system_role ? "success" : "info"}
                  sx={{ mt: 2 }}
                >
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      {roleStyle ? (
                        <Chip
                          label={selectedRole.name.charAt(0).toUpperCase() + selectedRole.name.slice(1)}
                          size="small"
                          sx={{ bgcolor: roleStyle.bg, color: roleStyle.color, fontWeight: 600 }}
                        />
                      ) : (
                        <Chip
                          label={selectedRole.is_system_role ? "Global" : "Custom"}
                          color={selectedRole.is_system_role ? "success" : "info"}
                          size="small"
                        />
                      )}
                      <Chip
                        label={bothPanels ? 'Both Panels' : 'Customer Panel'}
                        variant="outlined"
                        size="small"
                        sx={{
                          borderColor: bothPanels ? '#8b5cf6' : '#10b981',
                          color: bothPanels ? '#8b5cf6' : '#10b981',
                        }}
                      />
                    </Box>
                    <Typography variant="body2">
                      {selectedRole.description || "No description available"}
                    </Typography>
                    <Typography variant="caption" color="text.primary">
                      <i className="fa fa-shield-alt"></i>{" "}
                      {selectedRole.permissions.length} submodule permission
                      {selectedRole.permissions.length !== 1 ? "s" : ""}
                    </Typography>
                  </Box>
                </Alert>
              );
            })()}

            {formData.term_ids.length > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography fontWeight={600}>Terms Selected:</Typography>
                <Typography variant="body2">
                  User must accept {formData.term_ids.length} term(s) during
                  signup before accessing the workspace.
                </Typography>
              </Alert>
            )}

            <Alert severity="info" sx={{ mt: 3 }}>
              <Typography fontWeight={600}>How it works:</Typography>
              <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                <li>User receives an email invitation</li>
                <li>They verify email & set up password</li>
                {formData.term_ids.length > 0 && (
                  <li>They must accept assigned terms</li>
                )}
                <li>They access workspace with assigned role & panel access</li>
                <li>Admin/Employee roles access both panels; Customer/Student access Customer Panel only</li>
              </ul>
            </Alert>

            <Box textAlign="right" mt={3}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isLoading || loadingRoles || loadingTerms}
              >
                {isLoading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} /> Sending...
                  </>
                ) : (
                  <>
                    <i
                      className="fa fa-paper-plane"
                      style={{ marginRight: 6 }}
                    ></i>
                    Send Invitation
                  </>
                )}
              </Button>
            </Box>

            {error && (
              <Alert
                severity="error"
                sx={{ mt: 2 }}
                onClose={() => setError(null)}
              >
                {error}
              </Alert>
            )}

            {success && (
              <Alert
                severity="success"
                sx={{ mt: 2 }}
                onClose={() => setSuccess(null)}
              >
                {success}
              </Alert>
            )}
          </Box>
        </Box>
      </Grid>

      {/* Right: Available Roles & Terms Info */}
      <Grid item xs={12} md={6}>
        <Box
          sx={{
            p: 3,
            borderRadius: '12px',
            bgcolor: 'rgba(139,92,246,0.03)',
            border: '1px solid rgba(0,0,0,0.06)',
          }}
        >
          <Typography sx={{ fontFamily, fontSize: '1rem', fontWeight: 600, color: '#1e293b', mb: 2 }}>
            Available Roles & Terms
          </Typography>

          {loadingRoles ? (
            <Box textAlign="center" py={4}>
              <CircularProgress size={25} />
            </Box>
          ) : (
            <>
              {/* Roles Section */}
              <Box mb={3}>
                <Typography color="primary" fontWeight={600} mb={1}>
                  <i className="fa fa-users"></i> Roles
                </Typography>
                {globalRoles.length > 0 && (
                  <Box mb={2}>
                    <Typography color="success.main" variant="body2" mb={1}>
                      Global Roles
                    </Typography>
                    {globalRoles.map((role) => (
                      <Paper
                        key={role.id}
                        variant="outlined"
                        sx={{ p: 1.5, mb: 1, borderRadius: 1 }}
                      >
                        <Typography variant="body2" fontWeight={600}>
                          {role.name}
                        </Typography>
                        <Typography variant="caption">
                          {role.description}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                )}

                {customRoles.length > 0 && (
                  <Box>
                    <Typography color="info.main" variant="body2" mb={1}>
                      Custom Roles
                    </Typography>
                    {customRoles.map((role) => (
                      <Paper
                        key={role.id}
                        variant="outlined"
                        sx={{ p: 1.5, mb: 1, borderRadius: 1 }}
                      >
                        <Typography variant="body2" fontWeight={600}>
                          {role.name}
                        </Typography>
                        <Typography variant="caption">
                          {role.description}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Terms Section */}
              <Box>
                <Typography color="primary" fontWeight={600} mb={1}>
                  <i className="fa fa-file-text"></i> Available Terms
                </Typography>
                {loadingTerms ? (
                  <CircularProgress size={20} />
                ) : terms.length > 0 ? (
                  terms.map((term) => (
                    <Paper
                      key={term.id}
                      variant="outlined"
                      sx={{ p: 1.5, mb: 1, borderRadius: 1 }}
                    >
                      <Typography variant="body2" fontWeight={600}>
                        {term.title}
                      </Typography>
                      {term.description && (
                        <Typography variant="caption">
                          {term.description}
                        </Typography>
                      )}
                    </Paper>
                  ))
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    No active terms available
                  </Typography>
                )}
              </Box>
            </>
          )}
        </Box>
      </Grid>
    </Grid>
    </Box>
  );
}